/**
 * Compact estimation engine — a pure, deterministic, side-effect-free function.
 *
 * This is the intelligence under the "Estimate" step: an engineer answers a
 * handful of questions (what kind of change, what makes it complex, what extra
 * work it drags in, how well it's understood) and the engine returns a
 * transparent day-by-day breakdown, an engineering-effort total, a Fibonacci
 * story-point mapping, and a confidence score.
 *
 * Design goals:
 *   • DETERMINISTIC — same inputs always yield the same output (unit-tested).
 *   • TRANSPARENT   — every added day is itemized in `breakdown` so the number
 *                     is explainable, never a black box.
 *   • COMPACT       — a small, tunable set of additive factors, no ceremony.
 *
 * All effort is expressed in ideal engineering days. Spikes are special: they
 * are time-boxed investigations, so a spike returns a fixed timebox rather than
 * an additive estimate (complexity / extra-work do not inflate a spike).
 *
 * Pure TS — no React, no I/O, no globals mutated.
 */

/** The kind of change being estimated. Drives the base effort. */
export type ChangeType =
  | 'config'
  | 'bug'
  | 'feature'
  | 'multi_part'
  | 'cross_service_migration'
  | 'spike'
  | 'custom';

/** Factors that make a change riskier/larger. Each adds effort + lowers confidence. */
export type ComplexityFactor =
  | 'changes_prod_behavior'
  | 'legacy_unfamiliar'
  | 'never_done_before'
  | 'multiple_services';

/** Additional work a change commonly drags in. Each adds effort. */
export type ExtraWorkItem =
  | 'design'
  | 'integration_testing'
  | 'e2e_regression'
  | 'load_testing'
  | 'documentation'
  | 'new_infra'
  | 'mcm_approval'
  | 'cross_team';

/** How well-understood the work is. Drives confidence (and, for spikes, timebox). */
export type Unknowns = 'well_understood' | 'some' | 'major';

/** The estimation inputs. */
export interface EstimateInputs {
  changeType: ChangeType;
  /** Selected complexity factors (subset; order-independent, deduped). */
  complexity: ComplexityFactor[];
  /** Selected extra-work items (subset; order-independent, deduped). */
  extraWork: ExtraWorkItem[];
  /** How well-understood the work is. */
  unknowns: Unknowns;
  /**
   * For `changeType === 'custom'`: the engineer's free-text label for the
   * change (shown in the breakdown in place of a built-in change-type label).
   * Ignored for every other change type.
   */
  customChangeType?: string;
  /**
   * For `changeType === 'custom'`: the base effort in ideal days the engine
   * uses in place of a {@link BASE_DAYS} lookup. Ignored for other change
   * types; treated as 0 (never negative) when absent.
   */
  customBaseDays?: number;
}

/** A single itemized line in the estimate breakdown. */
export interface EstimateBreakdownItem {
  /** Human-readable label for this line. */
  label: string;
  /** Days this line contributes to the total. */
  days: number;
}

/** The estimation result. */
export interface EstimateResult {
  /** Transparent, itemized day-by-day breakdown (sums to engineeringEffortDays). */
  breakdown: EstimateBreakdownItem[];
  /** Total engineering effort in ideal days. */
  engineeringEffortDays: number;
  /** Fibonacci story points mapped from the effort. */
  storyPoints: number;
  /** Confidence percentage (0–100); starts high, drops with risk/unknowns. */
  confidence: number;
  /** True when the estimate is a time-boxed spike (see {@link timeboxDays}). */
  isSpike: boolean;
  /** For spikes: the recommended timebox in days. Undefined for non-spikes. */
  timeboxDays?: number;
}

// ---------------------------------------------------------------------------
// Tunable factor tables (all in ideal engineering days)
// ---------------------------------------------------------------------------

/** Base effort per change type. Spike is handled separately (timeboxed). */
const BASE_DAYS: Record<ChangeType, number> = {
  config: 0.5,
  bug: 1,
  feature: 3,
  multi_part: 5,
  cross_service_migration: 8,
  spike: 3, // fixed timebox anchor (see SPIKE_TIMEBOX_DAYS)
  custom: 0, // placeholder anchor — the real base comes from inputs.customBaseDays
};

/** Days added per complexity factor. */
const COMPLEXITY_DAYS: Record<ComplexityFactor, number> = {
  changes_prod_behavior: 1,
  legacy_unfamiliar: 1,
  never_done_before: 1.5,
  multiple_services: 1.5,
};

/** Days added per extra-work item. */
const EXTRA_WORK_DAYS: Record<ExtraWorkItem, number> = {
  design: 0.5,
  integration_testing: 1,
  e2e_regression: 1,
  load_testing: 1,
  documentation: 0.5,
  new_infra: 1,
  mcm_approval: 0.5,
  cross_team: 0.5,
};

/** Human-readable labels for the breakdown. */
const CHANGE_TYPE_LABELS: Record<ChangeType, string> = {
  config: 'Config change',
  bug: 'Bug fix',
  feature: 'Feature',
  multi_part: 'Multi-part change',
  cross_service_migration: 'Cross-service migration',
  spike: 'Spike',
  custom: 'Custom change',
};

const COMPLEXITY_LABELS: Record<ComplexityFactor, string> = {
  changes_prod_behavior: 'Changes production behavior',
  legacy_unfamiliar: 'Legacy / unfamiliar code',
  never_done_before: 'Never done before',
  multiple_services: 'Touches multiple services',
};

const EXTRA_WORK_LABELS: Record<ExtraWorkItem, string> = {
  design: 'Design',
  integration_testing: 'Integration testing',
  e2e_regression: 'E2E regression',
  load_testing: 'Load testing',
  documentation: 'Documentation',
  new_infra: 'New infrastructure',
  mcm_approval: 'MCM approval',
  cross_team: 'Cross-team coordination',
};

// ---------------------------------------------------------------------------
// Confidence tuning
// ---------------------------------------------------------------------------

/** Confidence a fully-understood, single-factor-free change starts at. */
const CONFIDENCE_START = 90;
/** Confidence lost per selected complexity factor. */
const CONFIDENCE_PER_COMPLEXITY = 8;
/** Confidence lost by the unknowns level. */
const CONFIDENCE_UNKNOWNS: Record<Unknowns, number> = {
  well_understood: 0,
  some: 10,
  major: 25,
};
/** Confidence floor — we never claim less than this. */
const CONFIDENCE_FLOOR = 40;

/** Spike timebox in days, chosen by how well-understood the investigation is. */
const SPIKE_TIMEBOX_DAYS: Record<Unknowns, number> = {
  well_understood: 2,
  some: 3,
  major: 5,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Rounds to 2 decimals to keep half-day arithmetic clean. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** De-dupes a list preserving first-seen order. */
function unique<T>(items: T[]): T[] {
  const seen = new Set<T>();
  const out: T[] = [];
  for (const item of items ?? []) {
    if (!seen.has(item)) {
      seen.add(item);
      out.push(item);
    }
  }
  return out;
}

/**
 * Maps engineering effort (ideal days) to Fibonacci story points.
 * <=1→1, <=2→2, <=3→3, <=5→5, <=8→8, <=13→13, else 21.
 */
export function effortToStoryPoints(effortDays: number): number {
  const d = effortDays;
  if (d <= 1) return 1;
  if (d <= 2) return 2;
  if (d <= 3) return 3;
  if (d <= 5) return 5;
  if (d <= 8) return 8;
  if (d <= 13) return 13;
  return 21;
}

/**
 * Computes the confidence score for a set of complexity factors + unknowns.
 * Starts at {@link CONFIDENCE_START}, subtracts per complexity factor and by
 * unknowns level, and is clamped to [{@link CONFIDENCE_FLOOR}, CONFIDENCE_START].
 */
export function computeConfidence(
  complexityCount: number,
  unknowns: Unknowns,
): number {
  const raw =
    CONFIDENCE_START -
    complexityCount * CONFIDENCE_PER_COMPLEXITY -
    CONFIDENCE_UNKNOWNS[unknowns];
  return Math.max(CONFIDENCE_FLOOR, Math.min(CONFIDENCE_START, raw));
}

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

/**
 * The pure estimation function. Given the inputs, returns a transparent
 * breakdown, an effort total, a Fibonacci story-point mapping, a confidence
 * score, and (for spikes) a recommended timebox.
 *
 * Spikes are time-boxed: the effort equals the timebox (chosen by the unknowns
 * level) and complexity / extra-work items do not inflate it — but they still
 * lower the confidence, reflecting that a fuzzier spike is a fuzzier bet.
 */
export function estimate(inputs: EstimateInputs): EstimateResult {
  const complexity = unique(inputs.complexity);
  const extraWork = unique(inputs.extraWork);
  const unknowns = inputs.unknowns;
  const confidence = computeConfidence(complexity.length, unknowns);

  if (inputs.changeType === 'spike') {
    const timeboxDays = SPIKE_TIMEBOX_DAYS[unknowns];
    return {
      breakdown: [
        { label: `Spike timebox (${unknowns.replace('_', ' ')})`, days: timeboxDays },
      ],
      engineeringEffortDays: round2(timeboxDays),
      storyPoints: effortToStoryPoints(timeboxDays),
      confidence,
      isSpike: true,
      timeboxDays,
    };
  }

  const breakdown: EstimateBreakdownItem[] = [];

  // For a custom change type the engineer supplies the base directly (never
  // negative); every other type looks it up in the fixed BASE_DAYS table.
  const base =
    inputs.changeType === 'custom'
      ? round2(Math.max(0, inputs.customBaseDays ?? 0))
      : BASE_DAYS[inputs.changeType];
  const baseLabel =
    inputs.changeType === 'custom'
      ? `${(inputs.customChangeType || CHANGE_TYPE_LABELS.custom).trim()} (base)`
      : `${CHANGE_TYPE_LABELS[inputs.changeType]} (base)`;
  breakdown.push({ label: baseLabel, days: base });

  for (const factor of complexity) {
    breakdown.push({
      label: COMPLEXITY_LABELS[factor],
      days: COMPLEXITY_DAYS[factor],
    });
  }

  for (const item of extraWork) {
    breakdown.push({
      label: EXTRA_WORK_LABELS[item],
      days: EXTRA_WORK_DAYS[item],
    });
  }

  const engineeringEffortDays = round2(
    breakdown.reduce((sum, line) => sum + line.days, 0),
  );

  return {
    breakdown,
    engineeringEffortDays,
    storyPoints: effortToStoryPoints(engineeringEffortDays),
    confidence,
    isSpike: false,
  };
}
